// T2-B event tracking dashboard (2026-05-04 PR 6b).
//
// Spec: design/event-tracking-spec.md §8
// Backend: omikuji-api PR 6a (#86) で実装、本番反映済 (api SHA eeef487+)。
//
// 3 タブ構成:
//   - 全体集計 (group_by 別 aggregate)
//   - ユーザ別 stats (user_id 検索)
//   - ランキング (motif / landmark / sponsor)
//
// CSV export はクライアント側で table → CSV 変換 (= 別ライブラリ不要、行数 hard cap 1000)。

import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/common/Select';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
import { todayIso, daysAgoIso } from '@/utils/utils';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

const GROUP_BY_OPTIONS = [
    { value: 'day', label: '日別' },
    { value: 'month', label: '月別' },
    { value: 'event_type', label: 'event_type 別' },
    { value: 'user', label: 'user 別' },
    { value: 'landmark', label: 'landmark 別' },
    { value: 'motif', label: 'motif 別' },
    { value: 'fortune', label: 'fortune 別' }
];

const EVENT_TYPE_OPTIONS = [
    { value: '', label: '(全 event)' },
    { value: 'shake_event', label: 'shake_event' },
    { value: 'landmark_visit', label: 'landmark_visit' },
    { value: 'coupon_acquired', label: 'coupon_acquired' },
    { value: 'coupon_redeemed', label: 'coupon_redeemed' },
    { value: 'collection_action', label: 'collection_action' },
    { value: 'app_session', label: 'app_session' },
    { value: 'subscription_event', label: 'subscription_event' }
];

const TOP_METRIC_OPTIONS = [
    { value: 'motif', label: 'motif (取得 motif 人気度)' },
    { value: 'landmark', label: 'landmark (訪問数)' },
    { value: 'sponsor', label: 'sponsor (coupon redeem 数)' }
];

const NONE_SENTINEL = '__none__';

function downloadCsv(filename: string, rows: any[]) {
    if (rows.length === 0) {
        toastify('error', 'no rows');
        return;
    }
    const cols = Object.keys(rows[0]);
    const escape = (v: any) => {
        if (v == null) return '';
        const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
        if (s.includes(',') || s.includes('"') || s.includes('\n')) {
            return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
    };
    const csv = [cols.join(','), ...rows.map(r => cols.map(c => escape(r[c])).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

type TabKey = 'aggregate' | 'stats' | 'top';

export default observer(function EventTrackingDashboardPage() {
    const {
        userEventsAdminStore: {
            aggregateRows,
            statsRows,
            topRows,
            topMetric,
            loading,
            fetchAggregate,
            fetchStats,
            fetchTop
        }
    } = useStore();

    const [tab, setTab] = useState<TabKey>('aggregate');

    // aggregate filters
    const [aggEventType, setAggEventType] = useState<string>('');
    const [aggGroupBy, setAggGroupBy] = useState<string>('day');
    const [aggFrom, setAggFrom] = useState<string>(`${daysAgoIso(30)}T00:00:00`);
    const [aggTo, setAggTo] = useState<string>(`${todayIso()}T23:59:59`);

    // stats filters
    const [statsUserId, setStatsUserId] = useState<string>('');
    const [statsYmFrom, setStatsYmFrom] = useState<string>('');
    const [statsYmTo, setStatsYmTo] = useState<string>('');

    // top filters
    const [topMetricInput, setTopMetricInput] = useState<string>('motif');
    const [topFrom, setTopFrom] = useState<string>(`${daysAgoIso(30)}T00:00:00`);
    const [topTo, setTopTo] = useState<string>(`${todayIso()}T23:59:59`);
    const [topLimit, setTopLimit] = useState<string>('20');

    const reloadAggregate = async () => {
        const res = await flowResult(fetchAggregate({
            event_type: aggEventType || undefined,
            group_by: aggGroupBy,
            from: aggFrom,
            to: aggTo
        }));
        if (!res?.ok) toastify('error', res?.message || '集計取得に失敗しました');
    };

    const reloadStats = async () => {
        const res = await flowResult(fetchStats({
            user_id: statsUserId ? Number(statsUserId) : undefined,
            year_month_from: statsYmFrom || undefined,
            year_month_to: statsYmTo || undefined
        }));
        if (!res?.ok) toastify('error', res?.message || 'stats 取得に失敗しました');
    };

    const reloadTop = async () => {
        const res = await flowResult(fetchTop({
            metric: topMetricInput,
            from: topFrom || undefined,
            to: topTo || undefined,
            limit: topLimit ? Number(topLimit) : 20
        }));
        if (!res?.ok) toastify('error', res?.message || 'ranking 取得に失敗しました');
    };

    return (
        <div className='space-y-4'>
            <h1 className='text-xl font-bold'>イベント計測ダッシュボード (T2-B)</h1>

            <div className='flex gap-2 border-b'>
                <button
                    type='button'
                    onClick={() => setTab('aggregate')}
                    className={`px-4 py-2 ${tab === 'aggregate' ? 'border-b-2 border-primary font-bold' : 'text-muted-foreground'}`}
                >
                    全体集計
                </button>
                <button
                    type='button'
                    onClick={() => setTab('stats')}
                    className={`px-4 py-2 ${tab === 'stats' ? 'border-b-2 border-primary font-bold' : 'text-muted-foreground'}`}
                >
                    ユーザ別
                </button>
                <button
                    type='button'
                    onClick={() => setTab('top')}
                    className={`px-4 py-2 ${tab === 'top' ? 'border-b-2 border-primary font-bold' : 'text-muted-foreground'}`}
                >
                    ランキング
                </button>
            </div>

            {tab === 'aggregate' && (
                <div className='space-y-3'>
                    <div className='grid grid-cols-2 lg:grid-cols-4 gap-3 items-end'>
                        <div>
                            <label className='text-xs text-muted-foreground'>event_type</label>
                            <Select
                                onValueChange={(v: string) => setAggEventType(v === NONE_SENTINEL ? '' : v)}
                                value={aggEventType || NONE_SENTINEL}
                            >
                                <SelectTrigger className='mt-1'><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {EVENT_TYPE_OPTIONS.map(o => (
                                        <SelectItem
                                            key={o.value || 'none'}
                                            value={o.value || NONE_SENTINEL}
                                            className='hover:cursor-pointer'
                                        >
                                            {o.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className='text-xs text-muted-foreground'>group_by</label>
                            <Select onValueChange={setAggGroupBy} value={aggGroupBy}>
                                <SelectTrigger className='mt-1'><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {GROUP_BY_OPTIONS.map(o => (
                                        <SelectItem key={o.value} value={o.value} className='hover:cursor-pointer'>
                                            {o.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className='text-xs text-muted-foreground'>from (ISO)</label>
                            <Input value={aggFrom} onChange={e => setAggFrom(e.target.value)} />
                        </div>
                        <div>
                            <label className='text-xs text-muted-foreground'>to (ISO)</label>
                            <Input value={aggTo} onChange={e => setAggTo(e.target.value)} />
                        </div>
                    </div>
                    <div className='flex gap-2'>
                        <Button type='button' onClick={reloadAggregate} disabled={loading}>
                            {loading ? '取得中...' : '集計'}
                        </Button>
                        <Button
                            type='button'
                            variant='outline'
                            onClick={() => downloadCsv(`aggregate-${Date.now()}.csv`, aggregateRows)}
                            disabled={aggregateRows.length === 0}
                        >
                            CSV ダウンロード
                        </Button>
                        <span className='text-sm text-muted-foreground self-center'>
                            {`${aggregateRows.length} 行 (hard cap 1000)`}
                        </span>
                    </div>
                    <div className='border rounded'>
                        <table className='w-full text-sm'>
                            <thead className='bg-background-6'>
                                <tr>
                                    <th className='px-3 py-2 text-left'>key</th>
                                    <th className='px-3 py-2 text-right'>count</th>
                                </tr>
                            </thead>
                            <tbody>
                                {aggregateRows.map((r: any, i: number) => (
                                    <tr key={i} className='border-t'>
                                        <td className='px-3 py-2'>{String(r.key ?? '(null)')}</td>
                                        <td className='px-3 py-2 text-right'>{r.count}</td>
                                    </tr>
                                ))}
                                {aggregateRows.length === 0 && (
                                    <tr><td colSpan={2} className='px-3 py-6 text-center text-muted-foreground'>(データなし)</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {tab === 'stats' && (
                <div className='space-y-3'>
                    <div className='grid grid-cols-2 lg:grid-cols-4 gap-3 items-end'>
                        <div>
                            <label className='text-xs text-muted-foreground'>user_id (空欄で全 user)</label>
                            <Input
                                type='number'
                                placeholder='例: 42'
                                value={statsUserId}
                                onChange={e => setStatsUserId(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className='text-xs text-muted-foreground'>year_month_from (YYYY-MM)</label>
                            <Input placeholder='2026-01' value={statsYmFrom} onChange={e => setStatsYmFrom(e.target.value)} />
                        </div>
                        <div>
                            <label className='text-xs text-muted-foreground'>year_month_to (YYYY-MM)</label>
                            <Input placeholder='2026-12' value={statsYmTo} onChange={e => setStatsYmTo(e.target.value)} />
                        </div>
                    </div>
                    <div className='flex gap-2'>
                        <Button type='button' onClick={reloadStats} disabled={loading}>
                            {loading ? '取得中...' : '取得'}
                        </Button>
                        <Button
                            type='button'
                            variant='outline'
                            onClick={() => downloadCsv(`user-stats-${Date.now()}.csv`, statsRows)}
                            disabled={statsRows.length === 0}
                        >
                            CSV ダウンロード
                        </Button>
                        <span className='text-sm text-muted-foreground self-center'>
                            {`${statsRows.length} 行 (hard cap 1000)`}
                        </span>
                    </div>
                    <div className='border rounded overflow-x-auto'>
                        <table className='w-full text-sm min-w-[1200px]'>
                            <thead className='bg-background-6'>
                                <tr>
                                    <th className='px-3 py-2 text-right'>user_id</th>
                                    <th className='px-3 py-2 text-left'>month</th>
                                    <th className='px-3 py-2 text-right'>shake</th>
                                    <th className='px-3 py-2 text-right'>landmark</th>
                                    <th className='px-3 py-2 text-right'>coupon_acq</th>
                                    <th className='px-3 py-2 text-right'>coupon_red</th>
                                    <th className='px-3 py-2 text-right'>motif_uniq</th>
                                    <th className='px-3 py-2 text-right'>share</th>
                                    <th className='px-3 py-2 text-right'>video</th>
                                    <th className='px-3 py-2 text-right'>print</th>
                                    <th className='px-3 py-2 text-right'>session(sec)</th>
                                    <th className='px-3 py-2 text-left'>fortune_breakdown</th>
                                </tr>
                            </thead>
                            <tbody>
                                {statsRows.map((r: any, i: number) => (
                                    <tr key={i} className='border-t'>
                                        <td className='px-3 py-2 text-right'>{r.user_id}</td>
                                        <td className='px-3 py-2'>{r.year_month}</td>
                                        <td className='px-3 py-2 text-right'>{r.shake_count}</td>
                                        <td className='px-3 py-2 text-right'>{r.landmark_visit_count}</td>
                                        <td className='px-3 py-2 text-right'>{r.coupon_acquired_count}</td>
                                        <td className='px-3 py-2 text-right'>{r.coupon_redeemed_count}</td>
                                        <td className='px-3 py-2 text-right'>{r.motif_unique_count}</td>
                                        <td className='px-3 py-2 text-right'>{r.share_count}</td>
                                        <td className='px-3 py-2 text-right'>{r.video_create_count}</td>
                                        <td className='px-3 py-2 text-right'>{r.print_count}</td>
                                        <td className='px-3 py-2 text-right'>{r.total_session_sec}</td>
                                        <td className='px-3 py-2 text-xs text-muted-foreground'>{JSON.stringify(r.fortune_tier_breakdown ?? {})}</td>
                                    </tr>
                                ))}
                                {statsRows.length === 0 && (
                                    <tr><td colSpan={12} className='px-3 py-6 text-center text-muted-foreground'>(データなし)</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {tab === 'top' && (
                <div className='space-y-3'>
                    <div className='grid grid-cols-2 lg:grid-cols-4 gap-3 items-end'>
                        <div>
                            <label className='text-xs text-muted-foreground'>metric</label>
                            <Select onValueChange={setTopMetricInput} value={topMetricInput}>
                                <SelectTrigger className='mt-1'><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {TOP_METRIC_OPTIONS.map(o => (
                                        <SelectItem key={o.value} value={o.value} className='hover:cursor-pointer'>
                                            {o.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className='text-xs text-muted-foreground'>from (ISO)</label>
                            <Input value={topFrom} onChange={e => setTopFrom(e.target.value)} />
                        </div>
                        <div>
                            <label className='text-xs text-muted-foreground'>to (ISO)</label>
                            <Input value={topTo} onChange={e => setTopTo(e.target.value)} />
                        </div>
                        <div>
                            <label className='text-xs text-muted-foreground'>limit (1-1000)</label>
                            <Input
                                type='number'
                                value={topLimit}
                                onChange={e => setTopLimit(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='flex gap-2'>
                        <Button type='button' onClick={reloadTop} disabled={loading}>
                            {loading ? '取得中...' : 'ランキング取得'}
                        </Button>
                        <Button
                            type='button'
                            variant='outline'
                            onClick={() => downloadCsv(`top-${topMetric || topMetricInput}-${Date.now()}.csv`, topRows)}
                            disabled={topRows.length === 0}
                        >
                            CSV ダウンロード
                        </Button>
                        <span className='text-sm text-muted-foreground self-center'>
                            {`${topRows.length} 行 / metric: ${topMetric || '(未取得)'}`}
                        </span>
                    </div>
                    <div className='border rounded'>
                        <table className='w-full text-sm'>
                            <thead className='bg-background-6'>
                                <tr>
                                    <th className='px-3 py-2 text-right w-16'>順位</th>
                                    <th className='px-3 py-2 text-left'>key</th>
                                    <th className='px-3 py-2 text-right'>count</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topRows.map((r: any, i: number) => (
                                    <tr key={i} className='border-t'>
                                        <td className='px-3 py-2 text-right'>{i + 1}</td>
                                        <td className='px-3 py-2'>{String(r.key ?? '(null)')}</td>
                                        <td className='px-3 py-2 text-right'>{r.count}</td>
                                    </tr>
                                ))}
                                {topRows.length === 0 && (
                                    <tr><td colSpan={3} className='px-3 py-6 text-center text-muted-foreground'>(データなし)</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
});
